import EditIcon from '@mui/icons-material/Edit';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Skeleton,Stack,TextField } from '@mui/material';
import Button from '@mui/material/Button';
import React,{ useContext,useEffect,useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { UserContext } from '../../context/UserContext';
import AttToken from '../../helpers/attToken';
import { trackEventMatomo } from '../../helpers/matomo';
import Companies from '../../service/Companies';
import type { HierarchyLabel } from '../../types/hierarchy';
import { Form } from '../../StyledComponents/Input/generic';

const companiesSrvc = new Companies();

export default function HierarchyConfig() {
  const { hierarchyLabel, saveHierarchyLabel } = useContext(UserContext);
  const [hierarchy, setHierarchy] = useState<HierarchyLabel>(hierarchyLabel);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('translation');

  useEffect(() => {
    setHierarchy(hierarchyLabel);
  }, [hierarchyLabel]);

  const editLabels = async (value: HierarchyLabel) => {
    setLoading(true);
    try {
      const token = await AttToken();
      if (token) {
        const response = await companiesSrvc.editHierarchyLabel({
          ...token, empresa: token.userEmpresa,
          labels: { ...value, default: false }
        });
        if (!response || typeof response !== 'object') throw new Error('Hierarchy labels were not updated.');
        response.message && toast.success(response.message, { toastId: String(response.message).substring(0, 20) });
        saveHierarchyLabel(value);
        setHierarchy(value);
      }
    } catch (err) {
      throw new Error('Error editing hierarchy labels');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form>
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="stretch"
        spacing={2}
      >
        <h1>{t('settingsPage.hierarchy.chooseLabel')}</h1>
        {loading ? <>
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} variant='rounded' height={40} />
          ))}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
          >
            <Skeleton variant='rounded' height={40} width={200} />
            <Skeleton variant='rounded' height={40} width={200} />
          </Stack>
        </> :
          <>
            <TextField
              size='small'
              onClick={() => trackEventMatomo('tela de label de hierarquia', 'click', 'textfield', 'edita valor')}
              label={`${hierarchyLabel.cliente} - (${t('settingsPage.hierarchy.client')})`}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setHierarchy({ ...hierarchy, cliente: e.target.value });
              }}
              value={hierarchy.cliente}
            />
            <TextField
              size='small'
              onClick={() => trackEventMatomo('tela de label de hierarquia', 'click', 'textfield', 'edita valor')}
              label={`${hierarchyLabel.local} - (${t('settingsPage.hierarchy.place')})`}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setHierarchy({ ...hierarchy, local: e.target.value });
              }}
              value={hierarchy.local}
            />
            <TextField
              size='small'
              onClick={() => trackEventMatomo('tela de label de hierarquia', 'click', 'textfield', 'edita valor')}
              label={`${hierarchyLabel.conjunto} - (${t('settingsPage.hierarchy.set')})`}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setHierarchy({ ...hierarchy, conjunto: e.target.value });
              }}
              value={hierarchy.conjunto}
            />
            <TextField
              size='small'
              onClick={() => trackEventMatomo('tela de label de hierarquia', 'click', 'textfield', 'edita valor')}
              label={`${hierarchyLabel.unidade} - (${t('settingsPage.hierarchy.unity')})`}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setHierarchy({ ...hierarchy, unidade: e.target.value });
              }}
              value={hierarchy.unidade}
            />
            <TextField
              size='small'
              onClick={() => trackEventMatomo('tela de label de hierarquia', 'click', 'textfield', 'edita valor')}
              label={`${hierarchyLabel.gerencia ? hierarchyLabel.gerencia : ''} - (${t('Reports.columns.manegement')})`}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setHierarchy({ ...hierarchy, gerencia: e.target.value });
              }}
              value={hierarchy.gerencia}
            />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
            >
              <Button
                onClick={(e) => {
                  trackEventMatomo('tela de configurações', 'click', 'form', 'edita hierarquia');
                  e.preventDefault();
                  editLabels(hierarchy);
                }}
                color='success'
                variant='contained'
                disabled={
                  hierarchy.cliente?.length < 3 ||
                  hierarchy.local?.length < 3 ||
                  hierarchy.conjunto?.length < 3 ||
                  hierarchy.unidade?.length < 3 ||
                  hierarchy.gerencia?.length < 3
                }
              >
                {t('settingsPage.hierarchy.changeBtn')}&nbsp;<EditIcon />
              </Button>
              <Button onClick={(e) => {
                e.preventDefault();
                editLabels({
                  cliente: 'Cliente',
                  local: 'Local',
                  conjunto: 'Conjunto',
                  unidade: 'Unidade',
                  gerencia: 'Gerência',
                });
              }} >{t('settingsPage.hierarchy.resetValues')}&nbsp;<RestartAltIcon /></Button>
            </Stack>
          </>}
      </Stack>
    </Form>
  );
};
